import {
  Mail,
  Globe,
  Shield,
  Eye,
  AlertOctagon,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronRight,
} from "lucide-react";
import type { CategorySummary } from "@/lib/simplify-results";

interface CategorySummaryCardsProps {
  categories: CategorySummary[];
  scanId?: string;
  className?: string;
  onCategoryClick?: (category: CategorySummary) => void;
}

interface SingleCategoryCardProps {
  category: CategorySummary;
  onClick?: () => void;
}

// Map category icon names to components
const CATEGORY_ICONS: Record<string, typeof Mail> = {
  Mail: Mail,
  Globe: Globe,
  Shield: Shield,
  Eye: Eye,
  AlertOctagon: AlertOctagon,
};

const STATUS_CONFIG: Record<CategorySummary["status"], {
  icon: typeof CheckCircle2;
  color: string;
  bg: string;
  border: string;
}> = {
  good: {
    icon: CheckCircle2,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
  },
  warning: {
    icon: AlertTriangle,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
  },
  critical: {
    icon: XCircle,
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
  },
};

function SingleCategoryCard({ category, onClick }: SingleCategoryCardProps) {
  const CategoryIcon = CATEGORY_ICONS[category.icon] || Shield;
  const statusConfig = STATUS_CONFIG[category.status];
  const StatusIcon = statusConfig.icon;

  return (
    <div
      onClick={onClick}
      className={`
        relative rounded-xl border ${statusConfig.border} ${statusConfig.bg}
        p-4 transition-all duration-200
        ${onClick ? "cursor-pointer hover:scale-[1.02]" : ""}
      `}
    >
      {/* Category icon and label */}
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2.5 rounded-lg bg-muted/50 border border-border/50">
          <CategoryIcon className="h-5 w-5 text-muted-foreground" />
        </div>
        <span className="font-medium text-sm text-foreground flex-1">
          {category.label}
        </span>
        {onClick && (
          <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
        )}
      </div>

      {/* Status display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusIcon className={`h-4 w-4 ${statusConfig.color}`} />
          <span className={`text-sm font-medium ${statusConfig.color}`}>
            {category.status === "good" ? "Good" : category.statusText}
          </span>
        </div>

        {category.issueCount > 0 && (
          <span
            className={`
              px-2 py-0.5 rounded-full text-xs font-mono font-bold
              ${statusConfig.bg} ${statusConfig.color}
              border ${statusConfig.border}
            `}
          >
            {category.issueCount}
          </span>
        )}
      </div>
    </div>
  );
}

export function CategorySummaryCards({ categories, onCategoryClick, className = "" }: CategorySummaryCardsProps) {
  if (categories.length === 0) {
    return null;
  }

  // Sort categories: critical first, then warning, then good
  const sortedCategories = [...categories].sort((a, b) => {
    const order = { critical: 0, warning: 1, good: 2 };
    return order[a.status] - order[b.status];
  });

  return (
    <div className={`rounded-xl border border-border bg-card/50 overflow-hidden ${className}`}>
      <div className="p-6 pb-4">
        <div className="flex items-center gap-3">
          <div className="h-6 w-1 rounded-full bg-cyan-500" />
          <div>
            <h3 className="font-semibold text-lg">Security Categories</h3>
            <p className="text-sm text-muted-foreground">
              Overview of your security across key areas
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 pb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {sortedCategories.map((category) => (
            <SingleCategoryCard
              key={category.category}
              category={category}
              onClick={onCategoryClick ? () => onCategoryClick(category) : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Compact horizontal version for smaller spaces
export function CategorySummaryRow({ categories, className = "" }: CategorySummaryCardsProps) {
  if (categories.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {categories.map((category) => {
        const CategoryIcon = CATEGORY_ICONS[category.icon] || Shield;
        const statusConfig = STATUS_CONFIG[category.status];
        const StatusIcon = statusConfig.icon;

        return (
          <div
            key={category.category}
            className={`
              inline-flex items-center gap-2 px-3 py-2 rounded-lg
              border ${statusConfig.border} ${statusConfig.bg}
            `}
          >
            <CategoryIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{category.label}</span>
            <StatusIcon className={`h-3.5 w-3.5 ${statusConfig.color}`} />
            {category.issueCount > 0 && (
              <span className={`text-xs font-mono font-bold ${statusConfig.color}`}>
                {category.issueCount}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default CategorySummaryCards;
